import { MemoCell } from "./memoCell";
import { PenaltyStatus } from "../../models/penaltyStatus";
import { useTranslation } from "react-i18next";
import { usePenalty } from "../../hooks/usePenalty";
import { TicketCheckInStatus } from "../../models/enum/ticketCheckInStatus";
import { PenaltyType } from "../../models/enum/penaltyType";
import { CacheKey } from "../../models/cacheKey";
import { useQueryClient } from "@tanstack/react-query";
import { WinnerIdInfo } from "../../models/winnerIdInfo";

export function PenaltyActions({
  penaltyStatus,
  onChangeStatus,
  artistId,
  eventId,
  rewardId,
  ticketCode,
  checkInStatus,
  fanIds,
}: {
  penaltyStatus?: PenaltyStatus;
  onChangeStatus: (state: PenaltyStatus) => void;
  artistId: number;
  eventId: number;
  rewardId: number;
  ticketCode: string | null;
  checkInStatus: TicketCheckInStatus;
  fanIds: WinnerIdInfo;
}) {
  const { penalize, isPending } = usePenalty({
    artistId,
    eventId,
    rewardId,
  });
  const queryClient = useQueryClient();
  const onChange = async (type: string, checked: boolean) => {
    await queryClient.invalidateQueries({
      queryKey: [CacheKey.getUserInfo, eventId, rewardId, ticketCode],
    });
    const newPenaltyStatus = new PenaltyStatus({
      ...penaltyStatus,
      [type]: checked,
    });
    onChangeStatus(newPenaltyStatus);
  };
  const { t } = useTranslation();
  const disableTardy = checkInStatus === TicketCheckInStatus.CHECKED_IN;

  return (
    <table className={"table"}>
      <tbody>
        <tr>
          <td colSpan={2}>
            <div className={"font-bold"}>{t("userdetail_note_subtitle")}</div>
            <div className={"pb-[0.75rem] font-bold"}></div>
            <div className={"label justify-start"}>
              <input
                checked={penaltyStatus?.tardy || false}
                type={"checkbox"}
                className={"toggle toggle-lg"}
                disabled={disableTardy || isPending}
                onChange={async (e) => {
                  await onChange("tardy", e.target.checked);
                }}
              />
              <span className={"label-text ml-3"}>
                {t("userdetail_note_late")}
              </span>
            </div>
            <div className={"label justify-start"}>
              <input
                checked={!!penaltyStatus?.transfer}
                type={"checkbox"}
                className={"toggle toggle-lg"}
                disabled={isPending}
                onChange={async (e) => {
                  const transfer = e.target.checked;
                  await penalize({
                    type: PenaltyType.TICKET_TRANSFER,
                    isCancel: !transfer,
                    userIds: fanIds,
                    violationId: penaltyStatus?.transfer?.id,
                  });
                  await onChange("transfer", transfer);
                }}
              />
              <span className={"label-text ml-3"}>
                {t("userdetail_note_transfer")}
              </span>
            </div>
            <div className={"label justify-start"}>
              <input
                checked={!!penaltyStatus?.violation}
                type={"checkbox"}
                className={"toggle toggle-lg"}
                disabled={isPending}
                onChange={async (e) => {
                  const violation = e.target.checked;
                  await penalize({
                    type: PenaltyType.RULE_VIOLATION,
                    isCancel: !violation,
                    userIds: fanIds,
                    violationId: penaltyStatus?.violation?.id,
                  });
                  await onChange("violation", violation);
                }}
              />
              <span className={"label-text ml-3"}>
                {t("userdetail_note_violation")}
              </span>
            </div>
          </td>
        </tr>
      </tbody>
      <MemoCell
        title={t("userdetail_note_status")}
        memo={penaltyStatus?.counts.print()}
      />
    </table>
  );
}
